import { SxProps, Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { commonStyles } from '~/theme/styles/commonStyles';

type TStyles = {
  paperContainer: SxProps<Theme>;
  title: SxProps<Theme>;
  container: SxProps<Theme>;
  item: SxProps<Theme>;
  row: SxProps<Theme>;
  table: SxProps<Theme>;
  clarifyingModalContainer: SxProps<Theme>;
  autocomplete: SxProps<Theme>;
  disabledInfo: SxProps<Theme>;
  answerCommentsContainer: SxProps<Theme>;
  tableInput: SxProps<Theme>;
  contragentBtnsContainer: SxProps<Theme>;
  infoIcon: SxProps<Theme>;
  checkboxContainer: SxProps<Theme>;
  licenseRow: Exclude<SxProps<Theme>, ReadonlyArray<unknown>>;
  rowWrapper: (start: number) => SxProps<Theme>;
};

const getRowStyles = (theme: Theme): SystemStyleObject<Theme> => ({
  boxSizing: 'border-box',
  borderBottom: `1px solid ${theme.palette.grey[100]}`,
  [theme.breakpoints.down('md')]: {
    background: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey[100]}`,
  },
});

export const styles: TStyles = {
  paperContainer: (theme: Theme) => ({
    padding: theme.spacing(3),
    background: theme.palette.background.default,
    boxShadow: `2px 2px 10px 0px ${theme.palette.grey.additionalGrey}85`,
    border: `1px solid ${theme.palette.grey.additionalGrey}`,
    gap: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  }),

  title: (theme: Theme) => ({
    letterSpacing: -1,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: theme.spacing(3),
    textAlign: 'center',
  }),

  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    position: 'relative',
    width: '100%',
  },

  item: (theme: Theme) => ({
    borderRight: `1px solid ${theme.palette.grey[100]}`,
    alignSelf: 'stretch',
    paddingTop: theme.spacing(1),
    alignItems: 'center',
    display: 'flex',
  }),

  row: (theme: Theme) => ({
    ...getRowStyles(theme),
    minHeight: theme.spacing(9),
  }),

  rowWrapper: (start: number) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    transform: `translateY(${start}px)`,
  }),

  licenseRow: (theme: Theme) => ({
    ...getRowStyles(theme),
  }),

  table: (theme: Theme) => ({
    ...(commonStyles.scroll(theme) as Object),
    ...(commonStyles.scrollWithBorder(theme) as Object),
    width: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: theme.spacing(60),
    overflowAnchor: 'none',
    background: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey[100]}`,
    [theme.breakpoints.down('md')]: {
      background: 'transparent',
      border: 'none',
      maxHeight: 'none',
    },
  }),

  clarifyingModalContainer: (theme: Theme) => ({
    ...(commonStyles.scroll(theme) as Object),
    width: '80%',
    overflowX: 'hidden',
  }),

  autocomplete: (theme: Theme) => ({
    mt: '14px',
    '& .MuiFormHelperText-root, .MuiAutocomplete-input': { pl: theme.spacing(2) },
    '& .MuiAutocomplete-endAdornment': { bottom: theme.spacing(-1), top: 'unset' },
    '& .MuiAutocomplete-tag': {
      width: '100%',
      height: theme.spacing(6),
      background: theme.palette.background.paper,
      color: theme.palette.text.primary,
      '&:hover': {
        background: `${theme.palette.background.paper}AA`,
      },
    },
    '& .MuiAutocomplete-inputRoot': {
      gap: theme.spacing(1),
    },
    width: '100%',
    '& .MuiInputBase-root': {
      flexWrap: 'wrap',
      height: 'auto',
      minHeight: '40px',
      alignItems: 'flex-start',
      paddingTop: '4px',
      paddingBottom: '4px',
    },
  }),

  tableInput: {
    '& .MuiFormHelperText-root, & .MuiInputBase-input': { pl: 2 },
  },

  disabledInfo: (theme: Theme) => ({
    color: theme.palette.grey.additionalGrey,
    background: theme.palette.grey.disabled,
    alignItems: 'center',
    display: 'flex',
    height: theme.spacing(6),
    width: '100%',
    mb: '14px',
  }),

  contragentBtnsContainer: (theme: Theme) => ({
    justifyContent: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    pr: theme.spacing(1),
  }),

  answerCommentsContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    width: '100%',
    mt: '14px',
  },

  infoIcon: (theme: Theme) => ({
    color: `${theme.palette.primary.main}70`,
    '&:hover': { color: theme.palette.primary.main },
  }),

  checkboxContainer: { p: 0, '& .MuiFormHelperText-root, .Mui-error': { bottom: '-14px' } },
};
